import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createDelivaryCondation,
  deleteDelivaryCondation,
  findDelivaryPostcodes,
  getAllDelivaryCondation,
  getDeliveryCoverageSummary,
  toggleDelivaryCondation,
  updateDelivaryCondation,
} from "@/api/settings/delivary-management";

export const fetchDeliveryCoverageSummary = createAsyncThunk(
  "delivaryManagement/fetchDeliveryCoverageSummary",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const res = await getDeliveryCoverageSummary(restaurantId);
      if (res?.status === "success") return res.data;
      return rejectWithValue(res?.message || "Failed to fetch coverage summary");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch coverage summary");
    }
  },
);

export const fetchDeliveryConditions = createAsyncThunk(
  "delivaryManagement/fetchDeliveryConditions",
  async ({ restaurantId, params }, { rejectWithValue }) => {
    try {
      const res = await getAllDelivaryCondation(restaurantId, params);
      if (res?.status === "success") return res.data;
      return rejectWithValue(res?.message || "Failed to fetch delivery conditions");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch delivery conditions");
    }
  },
);

export const fetchDeliveryPostcodes = createAsyncThunk(
  "delivaryManagement/fetchDeliveryPostcodes",
  async ({ restaurantId, params }, { rejectWithValue }) => {
    try {
      const res = await findDelivaryPostcodes(restaurantId, params);
      if (res?.status === "success") return res.data;
      return rejectWithValue(res?.message || "Failed to fetch post codes");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch post codes");
    }
  },
);

export const createDeliveryCondition = createAsyncThunk(
  "delivaryManagement/createDeliveryCondition",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createDelivaryCondation(payload);
      if (res?.status === "success") {
        return res?.data?.delivery_condition || res?.data || payload;
      }
      return rejectWithValue(res?.message || "Failed to create delivery condition");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create delivery condition");
    }
  },
);

export const updateDeliveryCondition = createAsyncThunk(
  "delivaryManagement/updateDeliveryCondition",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await updateDelivaryCondation(id, payload);
      if (res?.status === "success") {
        return res?.data?.delivery_condition || res?.data || { ...payload, id };
      }
      return rejectWithValue(res?.message || "Failed to update delivery condition");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update delivery condition");
    }
  },
);

export const deleteDeliveryCondition = createAsyncThunk(
  "delivaryManagement/deleteDeliveryCondition",
  async (id, { rejectWithValue }) => {
    try {
      const res = await deleteDelivaryCondation(id);
      if (res?.status === "success") return id;
      return rejectWithValue(res?.message || "Failed to delete delivery condition");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete delivery condition");
    }
  },
);

export const toggleDeliveryCondition = createAsyncThunk(
  "delivaryManagement/toggleDeliveryCondition",
  async (id, { rejectWithValue }) => {
    try {
      const res = await toggleDelivaryCondation(id);
      if (res?.status === "success") {
        return res?.data?.delivery_condition || res?.data || { id };
      }
      return rejectWithValue(res?.message || "Failed to toggle delivery condition");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to toggle delivery condition");
    }
  },
);

const initialState = {
  conditions: [],
  pagination: null,
  postcodes: [],
  postcodesMeta: null,
  coverageSummary: null,
  coverageLoading: false,
  coverageError: null,
  listLoading: false,
  postcodesLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  toggleLoading: false,
  success: null,
  error: null,
};

const deliveryManagementSlice = createSlice({
  name: "delivaryManagement",
  initialState,
  reducers: {
    clearDeliveryManagementMessages: (state) => {
      state.success = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeliveryCoverageSummary.pending, (state) => {
        state.coverageLoading = true;
        state.coverageError = null;
      })
      .addCase(fetchDeliveryCoverageSummary.fulfilled, (state, action) => {
        state.coverageLoading = false;
        state.coverageSummary = action.payload || null;
      })
      .addCase(fetchDeliveryCoverageSummary.rejected, (state, action) => {
        state.coverageLoading = false;
        state.coverageError = action.payload;
      })
      .addCase(fetchDeliveryConditions.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryConditions.fulfilled, (state, action) => {
        state.listLoading = false;
        state.conditions = action.payload?.delivery_conditions || [];
        state.pagination = action.payload?.pagination || null;
      })
      .addCase(fetchDeliveryConditions.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchDeliveryPostcodes.pending, (state) => {
        state.postcodesLoading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryPostcodes.fulfilled, (state, action) => {
        state.postcodesLoading = false;
        state.postcodes = action.payload?.items || [];
        state.postcodesMeta = {
          count: action.payload?.count,
          total: action.payload?.total,
          starting_distance: action.payload?.starting_distance,
          ending_distance: action.payload?.ending_distance,
          post_code_start_with: action.payload?.post_code_start_with,
        };
      })
      .addCase(fetchDeliveryPostcodes.rejected, (state, action) => {
        state.postcodesLoading = false;
        state.error = action.payload;
      })
      .addCase(createDeliveryCondition.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createDeliveryCondition.fulfilled, (state, action) => {
        state.createLoading = false;
        state.success = "Delivery condition created successfully";
        if (action.payload?.id) {
          state.conditions.unshift(action.payload);
        }
        if (state.coverageSummary?.bands && action.meta?.arg) {
          const arg = action.meta.arg;
          const configuredPostcodes = Array.isArray(arg.post_code)
            ? arg.post_code
            : [arg.post_code];
          const sDist = Number(arg.starting_distance);
          const eDist = Number(arg.ending_distance);

          state.coverageSummary = {
            ...state.coverageSummary,
            bands: state.coverageSummary.bands.map((band) => {
              if (
                Number(band.starting_distance) === sDist &&
                Number(band.ending_distance) === eDist
              ) {
                let newlyCoveredCount = 0;
                const updatedGroups = (band.groups || []).map((grp) => {
                  if (configuredPostcodes.includes(grp.postcode_first_part)) {
                    if (!grp.covered) {
                      newlyCoveredCount += Number(grp.postcode_count) || 0;
                    }
                    return {
                      ...grp,
                      covered: true,
                      covered_count: grp.postcode_count,
                      not_covered_count: 0,
                      partially_covered: false,
                    };
                  }
                  return grp;
                });

                const updatedCoveredCount =
                  (Number(band.covered_count) || 0) + newlyCoveredCount;

                return {
                  ...band,
                  covered_count: updatedCoveredCount,
                  groups: updatedGroups,
                };
              }
              return band;
            }),
          };
        }
      })
      .addCase(createDeliveryCondition.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })
      .addCase(updateDeliveryCondition.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateDeliveryCondition.fulfilled, (state, action) => {
        state.updateLoading = false;
        const updated = action.payload;
        if (updated?.id) {
          const index = state.conditions.findIndex((item) => item.id === updated.id);
          if (index !== -1) {
            state.conditions[index] = updated;
          }
        }
        state.success = "Delivery condition updated successfully";
      })
      .addCase(updateDeliveryCondition.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteDeliveryCondition.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteDeliveryCondition.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.conditions = state.conditions.filter((item) => item.id !== action.payload);
        state.success = "Delivery condition deleted successfully";
      })
      .addCase(deleteDeliveryCondition.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleDeliveryCondition.pending, (state) => {
        state.toggleLoading = true;
        state.error = null;
      })
      .addCase(toggleDeliveryCondition.fulfilled, (state, action) => {
        state.toggleLoading = false;
        const updated = action.payload;
        if (updated?.id) {
          const index = state.conditions.findIndex((item) => item.id === updated.id);
          if (index !== -1) {
            state.conditions[index] = {
              ...state.conditions[index],
              ...updated,
            };
          }
        }
        state.success = "Delivery condition status updated";
      })
      .addCase(toggleDeliveryCondition.rejected, (state, action) => {
        state.toggleLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearDeliveryManagementMessages } = deliveryManagementSlice.actions;
export default deliveryManagementSlice.reducer;
